(() => {
  'use strict';

  const VERSION = '9.5.0';
  const COOLDOWN_MS = 12000;
  const DUPLICATE_WINDOW_MS = 5 * 60 * 1000;
  const MAX_LINKS = 4;
  const PLACEHOLDER_EMAILS = new Set([
    'test@test.com', 'test@example.com', 'example@example.com', 'asdf@asdf.com',
    'abc@abc.com', 'email@email.com', 'name@email.com', 'none@none.com',
    'fake@fake.com', 'no@no.com'
  ]);
  const DISPOSABLE_DOMAINS = new Set([
    '10minutemail.com', 'dispostable.com', 'fakeinbox.com', 'guerrillamail.com',
    'guerrillamailblock.com', 'maildrop.cc', 'mailinator.com', 'mailnesia.com',
    'mintemail.com', 'sharklasers.com', 'temp-mail.org', 'tempmail.com',
    'throwawaymail.com', 'trashmail.com', 'yopmail.com'
  ]);
  const BAD_PHONE_PATTERNS = new Set([
    '0000000000', '1111111111', '1234567890', '2222222222', '3333333333',
    '4444444444', '5555555555', '6666666666', '7777777777', '8888888888',
    '9999999999', '0123456789', '0987654321'
  ]);

  const clean = (value) => String(value ?? '').trim();
  const normalize = (value) => clean(value).toLowerCase().replace(/\s+/g, ' ');
  const digits = (value) => clean(value).replace(/\D/g, '');
  const hash = (value) => {
    let result = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
      result ^= value.charCodeAt(i);
      result = Math.imul(result, 16777619);
    }
    return (result >>> 0).toString(36);
  };
  const storage = {
    get(key) { try { return localStorage.getItem(key); } catch (_) { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch (_) {} }
  };

  const isEligible = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.matches('[data-me-security-ignore], [data-type="add-to-cart-form"]')) return false;
    // Shopify Forms powers the managed bulk-quote submission and already owns
    // its validation / anti-abuse lifecycle. Do not inject our generic
    // honeypot, hidden fingerprint fields, cooldown logic, or submit capture
    // listener into that app-managed form. Mutating the form at submit time can
    // prevent Shopify Forms from completing its own submission transaction.
    if (form.closest('[data-me-group-order-quote]')) return false;
    if (form.closest('[data-me-wholesale-portal]') || form.querySelector('[data-me-wholesale-portal]')) return true;
    if (form.matches('.contact-form__form, .email-signup__form, [data-me-wed-form], [data-me-b2b-form], [data-me-secure-form]')) return true;
    const action = clean(form.getAttribute('action'));
    if (/contact|formspark|newsletter|account|comments/i.test(action)) return true;
    return Boolean(form.querySelector('input[type="email"]')) && !form.matches('[role="search"]');
  };

  const ensureStatus = (form) => {
    let status = form.querySelector('[data-me-security-status]');
    if (status) return status;
    status = document.createElement('div');
    status.className = 'me-form-security__status';
    status.dataset.meSecurityStatus = '';
    status.setAttribute('role', 'alert');
    status.setAttribute('aria-live', 'assertive');
    status.hidden = true;
    form.prepend(status);
    return status;
  };

  const ensureHoneypot = (form) => {
    let field = form.querySelector('[data-me-honeypot], input[name="website"], input[name="company_website"]');
    if (field) {
      field.dataset.meHoneypot = '';
      return field;
    }
    const wrapper = document.createElement('div');
    wrapper.className = 'me-form-security__trap';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.innerHTML = '<label>Leave this field empty<input type="text" name="company_website" tabindex="-1" autocomplete="off" data-me-honeypot></label>';
    form.appendChild(wrapper);
    return wrapper.querySelector('input');
  };

  const ensureHidden = (form, name, value = '') => {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
    return input;
  };

  const setError = (input, message) => {
    input.setCustomValidity(message || '');
    input.classList.toggle('is-invalid', Boolean(message));
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
  };

  const emailError = (input) => {
    const value = normalize(input.value);
    if (!value && input.required) return 'Enter your email address.';
    if (!value) return '';
    if (!input.validity.valid || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value)) return 'Enter a valid email address.';
    if (PLACEHOLDER_EMAILS.has(value) || /^(test|fake|asdf|abc|none|nope|sample)[+._-]?\d*@/i.test(value)) return 'Enter an email address you actively use.';
    const domain = value.split('@').pop();
    if (DISPOSABLE_DOMAINS.has(domain)) return 'Temporary or disposable email addresses are not accepted.';
    return '';
  };

  const phoneError = (input) => {
    const raw = clean(input.value);
    if (!raw && input.required) return 'Enter your phone number.';
    if (!raw) return '';
    const number = digits(raw);
    if (number.length === 11 && number.startsWith('1')) return BAD_PHONE_PATTERNS.has(number.slice(1)) ? 'Enter a valid phone number.' : '';
    if (number.length !== 10 || BAD_PHONE_PATTERNS.has(number)) return 'Enter a valid 10-digit U.S. phone number.';
    return '';
  };

  const formatPhone = (input) => {
    let number = digits(input.value);
    if (number.length === 11 && number.startsWith('1')) number = number.slice(1);
    number = number.slice(0, 10);
    if (number.length <= 3) input.value = number;
    else if (number.length <= 6) input.value = `(${number.slice(0, 3)}) ${number.slice(3)}`;
    else input.value = `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
  };

  const messageError = (input) => {
    const value = clean(input.value);
    const links = value.match(/(?:https?:\/\/|www\.)/gi)?.length || 0;
    if (links > MAX_LINKS) return 'Please remove extra links and include no more than four.';
    if (/(.)\1{14,}/.test(value)) return 'Please revise repeated or automated-looking text.';
    return '';
  };

  const formFingerprint = (form) => {
    const values = Array.from(form.elements)
      .filter((element) => element.name && !/password|token|captcha|website|company_website/i.test(element.name))
      .filter((element) => !['hidden', 'submit', 'button', 'file'].includes(element.type))
      .map((element) => `${element.name}:${normalize(element.value)}`)
      .join('|');
    return hash(`${location.pathname}|${values}`);
  };

  const clientSignals = (form) => {
    const email = form.querySelector('input[type="email"]');
    const phone = form.querySelector('input[type="tel"]');
    const flags = [];
    if (email && /@(gmail|yahoo|hotmail|outlook|icloud)\./i.test(email.value)) flags.push('consumer_email');
    if (phone && !clean(phone.value)) flags.push('phone_not_provided');
    if (navigator.webdriver) flags.push('browser_automation');
    if (Intl.DateTimeFormat().resolvedOptions().timeZone) flags.push(`timezone:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
    return flags;
  };

  const validate = (form, { report = false } = {}) => {
    const status = ensureStatus(form);
    const honeypot = ensureHoneypot(form);
    const errors = [];

    if (clean(honeypot?.value)) errors.push({ input: honeypot, message: 'Unable to submit this form.' });

    form.querySelectorAll('input[type="email"]').forEach((input) => {
      const message = emailError(input);
      setError(input, message);
      if (message) errors.push({ input, message });
    });
    form.querySelectorAll('input[type="tel"]').forEach((input) => {
      const message = phoneError(input);
      setError(input, message);
      if (message) errors.push({ input, message });
    });
    form.querySelectorAll('textarea').forEach((input) => {
      const message = messageError(input);
      setError(input, message);
      if (message) errors.push({ input, message });
    });

    if (!form.checkValidity()) {
      const invalid = form.querySelector(':invalid');
      if (invalid && !errors.some((item) => item.input === invalid)) errors.push({ input: invalid, message: invalid.validationMessage || 'Complete the required fields.' });
    }

    if (errors.length) {
      status.textContent = errors[0].message;
      status.hidden = false;
      if (report && errors[0].input !== honeypot) {
        errors[0].input?.focus();
        errors[0].input?.reportValidity?.();
      }
      return false;
    }
    status.hidden = true;
    status.textContent = '';
    return true;
  };

  const secure = (form) => {
    if (!isEligible(form) || form.dataset.meSecurityReady === 'true') return;
    form.dataset.meSecurityReady = 'true';
    form.dataset.meSecurityVersion = VERSION;
    ensureHoneypot(form);
    ensureHidden(form, 'me_security_version', VERSION);
    ensureHidden(form, 'me_client_risk_flags', '');
    ensureHidden(form, 'me_submission_fingerprint', '');

    form.querySelectorAll('input[type="tel"]').forEach((input) => {
      input.setAttribute('inputmode', 'tel');
      input.addEventListener('input', () => { formatPhone(input); setError(input, phoneError(input)); });
      input.addEventListener('blur', () => setError(input, phoneError(input)));
    });
    form.querySelectorAll('input[type="email"]').forEach((input) => input.addEventListener('blur', () => setError(input, emailError(input))));

    form.addEventListener('submit', (event) => {
      if (!validate(form, { report: true })) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const now = Date.now();
      const key = `me-form-security:${location.pathname}:${form.id || form.className}`;
      const last = Number(storage.get(`${key}:last`) || 0);
      if (last && now - last < COOLDOWN_MS) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const status = ensureStatus(form);
        status.textContent = 'Please wait a few seconds before submitting again.';
        status.hidden = false;
        return;
      }

      const fingerprint = formFingerprint(form);
      const previousFingerprint = storage.get(`${key}:fingerprint`);
      const previousTime = Number(storage.get(`${key}:fingerprint-time`) || 0);
      if (previousFingerprint === fingerprint && now - previousTime < DUPLICATE_WINDOW_MS) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const status = ensureStatus(form);
        status.textContent = 'It looks like this form was already submitted recently. Please wait before sending a duplicate.';
        status.hidden = false;
        return;
      }

      const flags = clientSignals(form);
      ensureHidden(form, 'me_client_risk_flags', flags.join(','));
      ensureHidden(form, 'me_submission_fingerprint', fingerprint);
      storage.set(`${key}:last`, String(now));
      storage.set(`${key}:fingerprint`, fingerprint);
      storage.set(`${key}:fingerprint-time`, String(now));
      window.dispatchEvent(new CustomEvent('me:secure-form-submit', { detail: { form, fingerprint, flags, version: VERSION } }));
    }, true);
  };

  const scan = (root = document) => root.querySelectorAll('form').forEach(secure);
  document.addEventListener('DOMContentLoaded', () => scan());
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  new MutationObserver((mutations) => mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
    if (!(node instanceof Element)) return;
    if (node.matches('form')) secure(node);
    scan(node);
  }))).observe(document.documentElement, { childList: true, subtree: true });

  window.MEFormSecurity = { scan, validate, version: VERSION };
})();
